import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { ISingleDuplicateCertificateApplication } from 'src/Types/module/applications/applications';
import MultipleAndSingleImgValidateWithPreview from 'src/common/FileValidation/MultipleAndSingleImgValidateWithPreview';
import { url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { IResponse } from 'src/basic-rest/types';
import { HttpClient } from 'src/Helpers/http/http';
import { useParams } from 'react-router-dom';
import { fetcherGet } from 'src/Utils/fetcher';
type IProps = {
  application: ISingleDuplicateCertificateApplication;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setApplication: React.Dispatch<
    React.SetStateAction<ISingleDuplicateCertificateApplication>
  >;
};

const UpdateDuplicateCertificateApp = ({
  application,
  setShowModal,
  setApplication,
}: IProps) => {
  const Toast = Toaster();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors, touchedFields },
    watch,
    getValues,
    reset,
    control,
  } = useForm<any>({
    defaultValues: {
      duplicate_certificate_application_status:
        application?.duplicate_certificate_application_status || 'pending',
    },
  });

  const isStatusReject = watch('duplicate_certificate_application_status');

  useEffect(() => {
    reset({
      ...application,
    });
    setLoading(false);
  }, [application, reset]);

  const onSubmit = async (value: any) => {
    const formData = new FormData();
    const allValues = getValues();

    const modifiedKeys = Array.from(
      new Set([
        ...Object.keys(touchedFields || {}),
        ...Object.keys(allValues).filter((key) => {
          const val = allValues[key];
          if (val instanceof FileList || Array.isArray(val)) {
            return val.length > 0 && val[0] instanceof File;
          }
          return (
            val !== undefined &&
            val !== null &&
            val !== (application as any)?.[key]
          );
        }),
      ])
    );

    if (modifiedKeys.length) {
      modifiedKeys.forEach((key) => {
        const val = allValues[key];
        if (val instanceof FileList || Array.isArray(val)) {
          const file = val[0];
          if (file instanceof File) {
            formData.append(key, file);
          }
        } else if (val !== undefined && val !== null) {
          formData.append(key, val);
        }
      });

      setLoading(true);
      try {
        const res: IResponse = await HttpClient.patch(
          `${url}/api/member/duplicate-certificate-application/${application.duplicate_certificate_application_id}`,
          formData
        );

        if (res.success) {
          Toaster().fire({
            icon: 'success',
            title: res.message,
          });
          const data = await fetcherGet(
            `/member/duplicate-certificate-application/${id}`
          );
          if (data?.success) {
            setApplication(data?.data);
          }
          setShowModal(false);
          reset({});
        }
      } catch (err: any) {
        Toaster().fire({
          icon: 'error',
          title: err?.response?.data?.message || 'Something went wrong',
        });
      } finally {
        setLoading(false);
      }
    } else {
      Toast.fire({
        icon: 'error',
        title: `Change field first`,
      });
    }
  };

  return (
    <>
      <Box component='form' onSubmit={handleSubmit(onSubmit)} p={0}>
        <Grid container spacing={2} mb={2}>
          <Grid xs={12} md={4} lg={4} item>
            <MultipleAndSingleImgValidateWithPreview
              mt={'0'}
              fileLimitSize={5}
              register={register}
              clearErrors={clearErrors}
              setError={setError}
              errors={errors}
              label='Duplicate Certificate Copy ( max size 5mb )'
              fieldName={`duplicate_certificate_application_gd_copy`}
              hideCustomLabel
              mode='single'
              imgSerUrl={[
                application?.duplicate_certificate_application_gd_copy,
              ]}
            />
          </Grid>

          <Grid xs={12} md={4} lg={4} item>
            <MultipleAndSingleImgValidateWithPreview
              mt={'0'}
              fileLimitSize={5}
              register={register}
              clearErrors={clearErrors}
              setError={setError}
              errors={errors}
              label='Duplicate Certificate Application file ( max size 5mb )'
              fieldName={`duplicate_certificate_application_application_file`}
              hideCustomLabel
              mode='single'
              imgSerUrl={[
                application?.duplicate_certificate_application_application_file,
              ]}
            />
          </Grid>
          <Grid xs={12} md={4} lg={4} item>
            <MultipleAndSingleImgValidateWithPreview
              mt={'0'}
              fileLimitSize={5}
              register={register}
              clearErrors={clearErrors}
              setError={setError}
              errors={errors}
              label={
                <Tooltip title='Duplicate Certificate Proof of Submission of Tax Return (Acknowledgement Receipts) file ( max size 5mb )'>
                  <span style={{ cursor: 'pointer' }}>
                    Duplicate Certificate Proof of Submission of Tax Return
                    (Acknowledgement Receipts) file ( max size 5mb )
                  </span>
                </Tooltip>
              }
              fieldName={`duplicate_certificate_application_tax_return`}
              hideCustomLabel
              mode='single'
              imgSerUrl={[
                application?.duplicate_certificate_application_tax_return,
              ]}
            />
          </Grid>
          <Grid xs={12} md={4} lg={4} item>
            <MultipleAndSingleImgValidateWithPreview
              mt={'0'}
              fileLimitSize={5}
              register={register}
              clearErrors={clearErrors}
              setError={setError}
              errors={errors}
              label='Duplicate Certificate trade license file ( max size 5mb )'
              fieldName={`duplicate_certificate_application_trade_license`}
              hideCustomLabel
              mode='single'
              imgSerUrl={[
                application?.duplicate_certificate_application_trade_license,
              ]}
            />
          </Grid>
          <Grid xs={12} md={4} lg={4} item>
            <MultipleAndSingleImgValidateWithPreview
              mt={'0'}
              fileLimitSize={5}
              register={register}
              clearErrors={clearErrors}
              setError={setError}
              errors={errors}
              label='Duplicate Certificate civil aviation file ( max size 5mb )'
              fieldName={`duplicate_certificate_application_civil_aviation`}
              hideCustomLabel
              mode='single'
              imgSerUrl={[
                application?.duplicate_certificate_application_civil_aviation,
              ]}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel id='demo-simple-select-label'>
                Select Status
              </InputLabel>
              <Controller
                name='duplicate_certificate_application_status'
                control={control}
                defaultValue={
                  application?.duplicate_certificate_application_status ||
                  'pending'
                }
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId='demo-simple-select-label'
                    label='Select Status'
                    value={field.value || 'pending'}
                  >
                    <MenuItem value='pending'>Pending</MenuItem>
                    <MenuItem value='approved'>Approved</MenuItem>
                    <MenuItem value='rejected'>Rejected</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          {isStatusReject === 'rejected' && (
            <Grid item xs={12} md={12}>
              <FormControl fullWidth>
                <TextField
                  placeholder='Write reason....'
                  id='outlined-basic'
                  label='Note'
                  variant='outlined'
                  {...register(
                    'duplicate_certificate_application_rejeceted_reason'
                  )}
                  multiline
                  type='text'
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Box mt={5}>
          <Button
            type={loading ? 'button' : 'submit'}
            fullWidth
            variant='contained'
            disableElevation
            onClick={(e) => {
              if (loading) e.preventDefault();
            }}
            sx={{
              py: 1.2,
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.85 : 1,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  color: '#ffffff',
                }}
              >
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                <span>Updating...</span>
              </Box>
            ) : (
              'Submit'
            )}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default UpdateDuplicateCertificateApp;
